import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Loader2, Upload, CheckCircle2, RotateCw, FlaskConical } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { Separator } from "@/frontend/components/ui/separator";
import { FormField } from "@/frontend/components/ui/form-field";
import { SegmentedControl } from "@/frontend/components/ui/segmented-control";
import { SectionLabel } from "@/frontend/components/ui/section-label";
import { InternalFhirView } from "@/frontend/components/dashboard/settings/InternalFhirView";
import { FhirServerForm } from "@/frontend/components/dashboard/settings/FhirServerForm";
import { orpc } from "@/frontend/orpc-client";
import { cn } from "@/frontend/lib/utils";
import { vendorFieldConfig, vendorUploadConfig, humanizeExportError, initialFormDrafts } from "@/frontend/utils/dashboard/settings/export";
import { DESTINATION_TYPE } from "@/server/types/export";
import type { CloudVendor } from "@/server/types/export";
import type {
  DestinationDialogType,
  ObjectStorageFormState,
  FhirServerFormState,
  FormDrafts,
  CredentialUploadState,
  FhirTab,
} from "@/frontend/types/dashboard/settings/export";
import { DIALOG_META } from "@/frontend/types/dashboard/settings/export";

export { initialFormDrafts };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: DestinationDialogType;
  formDrafts: FormDrafts;
  setFormDrafts: React.Dispatch<React.SetStateAction<FormDrafts>>;
  onSaveSuccess: () => void;
  destinationId?: string;
};

const FHIR_TAB_OPTIONS: ReadonlyArray<{ value: FhirTab; label: string }> = [
  { value: "test", label: "Internal FHIR Server" },
  { value: "custom", label: "Custom FHIR Server" },
];

export function AddDestinationDialog(props: Props) {
  const { open, onOpenChange, type, formDrafts, setFormDrafts, onSaveSuccess, destinationId } = props;
  const [error, setError] = useState<string | null>(null);
  const [fhirTab, setFhirTab] = useState<FhirTab>("test");
  const fhirTabRef = useRef<FhirTab>("test");
  const customDraftRef = useRef<FhirServerFormState>(initialFormDrafts().fhir_server);
  const wasOpen = useRef(open);
  const isEditing = !!destinationId;

  // Reset local state on open/close transitions
  if (open && !wasOpen.current) {
    setError(null);
  }
  if (!open && wasOpen.current) {
    setFhirTab("test");
    fhirTabRef.current = "test";
    setError(null);
    customDraftRef.current = initialFormDrafts().fhir_server;
  }
  wasOpen.current = open;

  const saveAndTest = useMutation(
    orpc.admin.saveAndTestDestination.mutationOptions()
  );

  const provisionTest = useMutation(
    orpc.admin.provisionTestFhirServer.mutationOptions()
  );

  const isFhirAdd = type === DESTINATION_TYPE.FHIR_SERVER && !isEditing;
  const isInternalFhirEdit =
    type === DESTINATION_TYPE.FHIR_SERVER &&
    isEditing &&
    formDrafts.fhir_server.isInternal;

  const internalFhirDraft = (result: { fhirBaseUrl: string; token: string }): FhirServerFormState => ({
    name: "Internal FHIR Server",
    fhirServerUrl: result.fhirBaseUrl,
    authType: "bearer_token" as const,
    bearerToken: result.token,
    tokenEndpoint: "", clientId: "", clientSecret: "", scope: "",
    isInternal: true,
  });

  const runProvision = () => {
    provisionTest.mutateAsync({})
      .then((r) => {
        if (fhirTabRef.current !== "test") return;
        setFormDrafts((prev) => ({ ...prev, fhir_server: internalFhirDraft(r) }));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  const handleFhirTabChange = (tab: FhirTab) => {
    setError(null);
    if (tab === "custom") {
      setFormDrafts((prev) => ({ ...prev, fhir_server: customDraftRef.current }));
    } else if (tab === "test") {
      if (provisionTest.data) {
        setFormDrafts((prev) => ({ ...prev, fhir_server: internalFhirDraft(provisionTest.data) }));
      } else if (!provisionTest.isPending) {
        runProvision();
      }
    }
    setFhirTab(tab);
    fhirTabRef.current = tab;
  };

  const handleSubmit = async () => {
    setError(null);
    const state = formDrafts[type];

    try {
      if (type === DESTINATION_TYPE.FHIR_SERVER) {
        const fs = state as FhirServerFormState;
        await saveAndTest.mutateAsync({
          ...(destinationId ? { destinationId } : {}),
          destinationType: DESTINATION_TYPE.FHIR_SERVER,
          name: fs.name,
          fhirServerUrl: fs.fhirServerUrl,
          authType: fs.authType,
          tokenEndpoint: fs.tokenEndpoint || null,
          clientId: fs.clientId || null,
          clientSecret: fs.clientSecret || null,
          scope: fs.scope || null,
          bearerToken: fs.bearerToken || null,
        });
      } else {
        const objStoreState = state as ObjectStorageFormState;
        await saveAndTest.mutateAsync({
          ...(destinationId ? { destinationId } : {}),
          destinationType: DESTINATION_TYPE.OBJECT_STORAGE,
          name: objStoreState.name,
          cloudVendor: type as CloudVendor,
          endpoint: objStoreState.endpoint,
          region: objStoreState.region,
          bucket: objStoreState.bucket,
          accessKeyId: objStoreState.accessKeyId,
          secretAccessKey: objStoreState.secretAccessKey,
          fileType: objStoreState.fileType,
        });
      }
      onSaveSuccess();
    } catch (err) {
      setError(humanizeExportError(err instanceof Error ? err.message : String(err)));
    }
  };

  const meta = DIALOG_META[type];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[460px] p-0 gap-0"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (isFhirAdd) runProvision();
        }}
        onCloseAutoFocus={() => {
          provisionTest.reset();
        }}
      >
        <div className="px-6 pt-5 pb-0">
          <DialogTitle className="text-[15px]">{isEditing ? meta.editTitle : meta.addTitle}</DialogTitle>
          <p className="text-[11px] text-muted-foreground mt-1 mb-4">
            {meta.subtitle}
          </p>

          {isFhirAdd && (
            <div className="mb-4">
              <SegmentedControl
                value={fhirTab}
                onChange={handleFhirTabChange}
                options={FHIR_TAB_OPTIONS}
                size="lg"
                variant="emphasis"
              />
            </div>
          )}

          {error && (
            <div className={cn(
              "flex items-start gap-2 p-3 mb-4 rounded-lg min-w-0",
              "bg-red-500/5 border border-red-500/20"
            )}>
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <span className="text-[11px] text-red-300 break-all">
                  {error}
                </span>
                {isFhirAdd && fhirTab === "test" && (
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      runProvision();
                    }}
                    className="text-[10px] text-red-400 hover:text-red-300 inline-flex items-center gap-1 self-start"
                  >
                    <RotateCw className="w-3 h-3" /> Retry
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {type === DESTINATION_TYPE.FHIR_SERVER ? (
          isFhirAdd && fhirTab === "test" ? (
            provisionTest.isPending ? (
              <div className="px-6 py-10 flex flex-col items-center justify-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <p className="text-[11px] text-muted-foreground">Provisioning server...</p>
              </div>
            ) : provisionTest.data ? (
              <InternalFhirView state={formDrafts.fhir_server} />
            ) : null
          ) : isInternalFhirEdit ? (
            <InternalFhirView state={formDrafts.fhir_server} />
          ) : (
            <FhirServerForm
              state={formDrafts.fhir_server}
              onChange={(s) => {
                if (isFhirAdd) customDraftRef.current = s;
                setFormDrafts((prev) => ({ ...prev, fhir_server: s }));
              }}
            />
          )
        ) : (
          <ObjectStorageForm
            type={type}
            state={formDrafts[type]}
            onChange={(s) => setFormDrafts((prev) => ({ ...prev, [type]: s }))}
            editAccessKeyId={isEditing ? (formDrafts[type] as ObjectStorageFormState).accessKeyId || undefined : undefined}
          />
        )}

        <div className="flex justify-end items-center gap-3 px-6 py-3.5 border-t bg-muted/20">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {isInternalFhirEdit ? "Close" : "Cancel"}
          </Button>
          {!isInternalFhirEdit && (
            <Button
              onClick={handleSubmit}
              disabled={saveAndTest.isPending || (isFhirAdd && fhirTab === "test" && !provisionTest.isSuccess)}
            >
              {saveAndTest.isPending ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{isEditing ? "Updating..." : "Saving..."}</>
              ) : (
                isEditing ? "Update" : "Save"
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Object Storage Form

/**
 * Renders the object storage destination form (AWS, GCS, or Azure).
 * For AWS/GCS, shows a credential file upload component; for Azure, shows manual input fields.
 *
 * 1. Renders connection fields (name, endpoint, region, bucket)
 * 2. Conditionally renders CredentialUpload for GCS/AWS or manual inputs for Azure
 * 3. Renders file type selector (NDJSON or Zipped NDJSON)
 *
 * @param type - The cloud vendor type
 * @param state - Current form state
 * @param onChange - Callback to update form state
 * @param editAccessKeyId - When editing, the existing access key ID to pre-populate the upload card
 */
function ObjectStorageForm({
  type, state, onChange, editAccessKeyId,
}: {
  type: "aws" | "gcs" | "azure";
  state: ObjectStorageFormState;
  onChange: (s: ObjectStorageFormState) => void;
  editAccessKeyId?: string;
}) {
  const config = vendorFieldConfig[type];
  const uploadConfig = vendorUploadConfig[type];
  const [credUpload, setCredUpload] = useState<CredentialUploadState | null>(() => {
    if (editAccessKeyId && uploadConfig) {
      const maskedKeyId = type === "aws" && editAccessKeyId.length > 8
        ? editAccessKeyId.slice(0, 4) + "••••••••" + editAccessKeyId.slice(-4)
        : editAccessKeyId;
      const displayFields: { label: string; value: string }[] =
        type === "gcs"
          ? [
              { label: "Service Account", value: editAccessKeyId },
              { label: "Private Key", value: "••••••••••••" },
            ]
          : [
              { label: "Access Key ID", value: maskedKeyId },
              { label: "Secret Access Key", value: "••••••••••••" },
            ];
      return { uploaded: true, filename: "existing credentials", displayFields };
    }
    return null;
  });
  const [uploadError, setUploadError] = useState<string | null>(null);

  const update = (field: string, value: string) =>
    onChange({ ...state, [field]: value });

  /**
   * Reads the uploaded file, parses it via the vendor-specific parser,
   * fills form state fields, and sets the summary card display state.
   *
   * @param file - The uploaded credential file
   */
  const handleFileUpload = (file: File) => {
    setUploadError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = uploadConfig!.parse(reader.result as string);
        onChange({
          ...state,
          accessKeyId: result.accessKeyId,
          secretAccessKey: result.secretAccessKey,
          ...(result.region ? { region: result.region } : {}),
          ...(result.endpoint ? { endpoint: result.endpoint } : {}),
        });
        setCredUpload({
          uploaded: true,
          filename: file.name,
          displayFields: result.displayFields,
        });
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : "Failed to parse file");
      }
    };
    reader.readAsText(file);
  };

  /**
   * Removes the uploaded credential file, resets the upload state,
   * and clears the access key fields in the form.
   */
  const handleRemoveUpload = () => {
    setCredUpload(null);
    setUploadError(null);
    onChange({ ...state, accessKeyId: "", secretAccessKey: "" });
  };

  return (
    <>
      <div className="px-6 pb-4 space-y-3">
        <FormField
          label="Destination Name"
          placeholder="e.g. prod-patient-export"
          value={state.name}
          onChange={(e) => update("name", e.target.value)}
        />
        {type !== "gcs" && (
          <div className="grid grid-cols-[2fr_1fr] gap-3">
            <FormField
              label="Endpoint"
              placeholder={config.endpointPlaceholder}
              value={state.endpoint}
              onChange={(e) => update("endpoint", e.target.value)}
            />
            <FormField
              label={config.regionLabel}
              placeholder={config.regionPlaceholder}
              value={state.region}
              onChange={(e) => update("region", e.target.value)}
            />
          </div>
        )}
        <FormField
          label={config.bucketLabel}
          placeholder={config.bucketPlaceholder}
          value={state.bucket}
          onChange={(e) => update("bucket", e.target.value)}
        />
      </div>

      <Separator />

      <div className="px-6 py-4">
        <SectionLabel>Credentials</SectionLabel>
        {uploadConfig ? (
          <>
            <CredentialUpload
              vendorType={type as "aws" | "gcs"}
              uploadState={credUpload}
              onUpload={handleFileUpload}
              onRemove={handleRemoveUpload}
            />
            {uploadError && (
              <p className="text-[11px] text-red-400 mt-2">{uploadError}</p>
            )}
          </>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <FormField
              label={config.accessKeyLabel}
              placeholder={config.accessKeyPlaceholder}
              value={state.accessKeyId}
              onChange={(e) => update("accessKeyId", e.target.value)}
            />
            <FormField
              label={config.secretKeyLabel}
              placeholder="••••••••"
              type="password"
              value={state.secretAccessKey}
              onChange={(e) => update("secretAccessKey", e.target.value)}
            />
          </div>
        )}
      </div>

      <Separator />

      <div className="px-6 py-4">
        <SectionLabel>Format</SectionLabel>
        <div className="flex gap-0.5 bg-muted/40 rounded-lg p-1 h-9">
          {(["ndjson", "zipped-ndjson"] as const).map((ft) => (
            <button
              key={ft}
              type="button"
              onClick={() => update("fileType", ft)}
              className={cn(
                "flex-1 px-4 text-center text-xs font-medium rounded-md transition-colors",
                state.fileType === ft
                  ? "bg-background text-blue-500 shadow-sm"
                  : "text-foreground/70 hover:text-foreground hover:bg-muted/60"
              )}
            >
              {ft === "ndjson" ? "NDJSON" : "Zipped NDJSON"}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * Renders either a file upload button or a summary card for credential files.
 *
 * 1. If no file uploaded: shows a description label + upload button
 * 2. After upload: shows a summary card with parsed values and a Remove/Replace link
 * 3. On parse error: shows inline red error text below the upload button
 *
 * @param vendorType - The cloud vendor type ("aws" or "gcs")
 * @param uploadState - Current upload state, or null if no file uploaded
 * @param onUpload - Callback when a file is selected for upload
 * @param onRemove - Callback to remove the uploaded credential file
 */
function CredentialUpload({
  vendorType,
  uploadState,
  onUpload,
  onRemove,
}: {
  vendorType: "aws" | "gcs";
  uploadState: CredentialUploadState | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const config = vendorUploadConfig[vendorType]!;

  /**
   * Handles file input change event, passes the file to onUpload,
   * and resets the input value so the same file can be re-selected.
   *
   * @param e - The file input change event
   */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (uploadState?.uploaded) {
    return (
      <div className="bg-background border border-green-500/20 rounded-lg p-3.5">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <span className="text-xs font-medium">{uploadState.filename}</span>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-muted-foreground underline hover:text-foreground transition-colors"
          >
            {uploadState.filename === "existing credentials" ? "Replace" : "Remove"}
          </button>
        </div>
        <div className="space-y-1.5">
          {uploadState.displayFields.map((f) => (
            <div key={f.label} className="flex justify-between text-xs">
              <span className="text-muted-foreground">{f.label}</span>
              <span className="text-foreground/80 font-mono truncate ml-4 max-w-[220px]">{f.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{config.description}</span>
        <Button
          type="button"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="gap-1.5"
        >
          <Upload className="w-3.5 h-3.5" />
          {config.buttonLabel}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={config.accept}
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    </div>
  );
}
